import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { cameraService } from "client-app-core";
import { simpleUnsub } from "orion-components/ContextualData/contextStreaming";
import { CircularProgress, Typography } from "@mui/material";
import { Translate, getTranslation } from "orion-components/i18n";
import { useDispatch } from "react-redux";
import EventBus from "orion-components/SharedComponents/EventBus";
import StreamFailed from "../WebRTCPlayer/Wowza/utils/StreamFailed";

// Standard playback speeds supported by WSImagePlayer
const STANDARD_SPEED_RANGE = [-16, -8, -4, -2, -1, -0.5, 0.5, 1, 2, 4, 8, 16];

const propTypes = {
	cameraId: PropTypes.string.isRequired,
	instanceId: PropTypes.string,
	videoProfile: PropTypes.string,
	resizeTo: PropTypes.string,
	entityId: PropTypes.string,
	playBarValue: PropTypes.oneOfType([PropTypes.date, PropTypes.object]),
	entityType: PropTypes.string,
	displayProgress: PropTypes.bool,
	playbackStartTime: PropTypes.string,
	playbackPlaying: PropTypes.bool,
	currentReplayMedia: PropTypes.array,
	addReplayMedia: PropTypes.func,
	removeReplayMedia: PropTypes.func,
	imageChange: PropTypes.func,
	selectedVideoMode: PropTypes.string,
	api: PropTypes.string
};

const defaultProps = {
	instanceId: "",
	videoProfile: "desktop",
	resizeTo: "dock",
	entityId: "",
	entityType: "",
	displayProgress: true,
	playbackStartTime: null,
	playbackPlaying: null,
	currentReplayMedia: [],
	addReplayMedia: () => { },
	removeReplayMedia: () => { },
	selectedVideoMode: "live",
	api: "liveStream"
};

const WSImagePlayer = props => {
	const {
		cameraId,
		videoProfile,
		resizeTo,
		instanceId,
		entityType,
		playbackStartTime,
		entityId,
		playBarValue,
		playbackPlaying,
		removeReplayMedia,
		currentReplayMedia,
		addReplayMedia,
		imageChange,
		displayProgress,
		selectedVideoMode,
		api
	} = props;
	const dispatch = useDispatch();

	const [videoData, setVideoData] = useState(null);
	const [metadata, setMetadata] = useState(null);
	const [subscription, setSubscription] = useState(null);
	const [error, setError] = useState(null);
	const [errorMessage, setErrorMessage] = useState(
		getTranslation("global.CBComponents.video.wsImagePlayer.encounteredText")
	);
	const [playbackVideoId, setPlaybackVideoId] = useState(null);
	const [playbackStreamStarted, setPlaybackStreamStarted] = useState(false);
	const [replaySetupStream, setReplaySetupStream] = useState(false);
	const [recoverPlaybackStreamState, setRecoverPlaybackStreamState] = useState();
	const [videoMode, setVideoMode] = useState(null);

	const prevPropCameraId = useRef();
	const prevPropPlaybackPlaying = useRef();
	const prevPropCurrentReplayMedia = useRef();
	const videoStreamId = useRef();
	const clip = useRef({});

	const imageRef = useRef();
	const playerDiv = useRef();

	const isMounted = useRef(false);

	useEffect(() => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);

	// If stream, kill it on unmount
	useEffect(() => {
		return () => {
			if (subscription && subscription.unsubscribe) {
				subscription.unsubscribe();
			}
		};
	}, [subscription]);

	useEffect(() => {
		if (playerDiv.current) {
			setPlayerDimensions(playerDiv.current.offsetWidth, playerDiv.current.offsetHeight);
		}
	}, [resizeTo]);

	useEffect(() => {
		if (subscription) {
			simpleUnsub(subscription.channel);
		}
		if (selectedVideoMode !== videoMode) {
			if (selectedVideoMode.toLowerCase() === "recorded") {
				EventBus.subscribe("Universal_PlaybackCommand", execPlaybackCommand);
			} else if (videoMode) {
				EventBus.unsubscribe("Universal_PlaybackCommand", execPlaybackCommand);
			}
			setVideoMode(selectedVideoMode);
			videoStreamId.current = null;
			if (selectedVideoMode.toLowerCase() === "live") playVideo();
		}
		return () => {
			if (videoMode === "recorded") {
				EventBus.unsubscribe("Universal_PlaybackCommand", execPlaybackCommand);
			}
		};
	}, [selectedVideoMode]);

	// If camera changes, unsub old camera and sub to new camera
	useEffect(() => {
		// If camera is different on prop change
		if (prevPropCameraId.current !== cameraId) {
			prevPropCameraId.current = cameraId;
			// If subscription, unsub
			if (subscription) {
				simpleUnsub(subscription.channel);
			}

			if (playbackVideoId) {
				// -- if there is a playback video loaded, decrement the loadedVideos state count
				dispatch(removeReplayMedia(cameraId));
			}

			// Clear out state
			setVideoData(null);
			setSubscription(null);
			setError(null);
			setPlaybackVideoId(null);
			setPlaybackStreamStarted(false);
			setRecoverPlaybackStreamState(false);

			if (api == "liveStream") {
				// Start new video
				if (!playbackStartTime) {
					playVideo();
				} else {
					setupPlaybackStream(cameraId, videoProfile, playbackStartTime);
				}
			}
		} else {
			if (api == "liveStream") {
				// -- Pause/Play playback stream if applicable
				if (playbackVideoId && prevPropPlaybackPlaying.current !== playbackPlaying) {
					if (playbackPlaying) {
						startPlaybackStream(cameraId, playbackVideoId);
					} else {
						pausePlaybackStream(cameraId, playbackVideoId);
					}
					prevPropPlaybackPlaying.current = playbackPlaying;
				}
			}
		}

		// -- FOR REPLAY - restart playback video if new media (audio or video) has been loaded
		if (
			prevPropCurrentReplayMedia.current !== currentReplayMedia &&
			currentReplayMedia.length > 0 &&
			playbackStartTime
		) {
			prevPropCurrentReplayMedia.current = currentReplayMedia;
			// -- restart playback video if the new media is not this camera
			const newMediaId = currentReplayMedia.filter(
				media => !prevPropCurrentReplayMedia.current.includes(media)
			)[0];
			if (newMediaId && newMediaId !== cameraId) {
				// If subscription, unsub
				if (subscription) {
					simpleUnsub(subscription.channel);
				}
				// Clear out state
				setVideoData(null);
				setSubscription(null);
				setError(null);
				setPlaybackVideoId(null);
				setPlaybackStreamStarted(false);
				// -- Establish a new stream at current play bar value without firing the videoUpdated event
				restartPlaybackStream(cameraId, videoProfile, playbackStartTime);
			}
		}

		if (recoverPlaybackStreamState) {
			// Clear out state
			setVideoData(null);
			setSubscription(null);
			setError(null);
			setPlaybackVideoId(null);
			setPlaybackStreamStarted(false);
			setRecoverPlaybackStreamState(false);
			// -- Establish a new stream without firing the videoUpdated event
			restartPlaybackStream(cameraId, videoProfile, playBarValue, true);
		}
	}, [cameraId, playbackVideoId, recoverPlaybackStreamState]);

	const playVideo = () => {
		if (subscription) {
			simpleUnsub(subscription.channel);
		}

		// Grid virtualization can dispatch playback commands while this cell player is
		// mounting/unmounting. Guard null refs to avoid crashing the tree.
		const playerWidth = playerDiv.current?.offsetWidth || 640;
		const playerHeight = playerDiv.current?.offsetHeight || 360;

		// -- Live video
		const options = {
			instanceId: instanceId,
			playerWidth,
			playerHeight,
			type: selectedVideoMode,
			api
		};
		if (entityId) options["entityId"] = entityId;
		if (entityType) options["entityType"] = entityType;

		if (clip.current.start && selectedVideoMode.toLowerCase() === "recorded") {
			options["playbackStartTime"] = clip.current.start;
		}

		const timeoutId = setTimeout(() => {
			if (subscription) {
				simpleUnsub(subscription.channel);
			}
			setError(true);
		}, 10000);

		cameraService
			.streamVideo(cameraId, videoProfile, options, (err, response) => {
				clearTimeout(timeoutId);
				if (err) console.error(err);
				if (!response || !isMounted.current) return;

				const { videoId, Data, timestamp, metadata } = response;
				if (response.err) {
					console.error("Error during WSImagePlayer streamVideo operation:", response.err);
					if (subscription) {
						simpleUnsub(subscription.channel);
					}
					setError(true);
				} else if (Data) {
					const firstImage = videoStreamId.current ? false : true;
					if (clip) checkLoop(timestamp);
					videoStreamId.current = videoId;
					if (firstImage) {
						if (
							selectedVideoMode.toLowerCase() === "recorded" &&
							clip.current.start &&
							Math.abs(new Date(timestamp) - clip.current.start) > 1000
						) {
							setErrorMessage(
								getTranslation("global.CBComponents.video.wsImagePlayer.recordingNotFound")
							);
							setError(true);
							return;
						}
						if (selectedVideoMode.toLowerCase() === "recorded" && clip.current.start)
							setPlaybackSpeed(clip.current.speed);

						// Publish player capabilities when stream starts (only in recorded mode)
						if (selectedVideoMode.toLowerCase() === "recorded") {
							console.debug(
								`[WSImagePlayer][${instanceId}] Publishing capabilities (first image in recorded mode):`,
								STANDARD_SPEED_RANGE
							);
							EventBus.publish("VideoPlayer_CapabilitiesReady", {
								instanceId,
								supportedSpeeds: STANDARD_SPEED_RANGE
							});
						}
					}
					setVideoData("data:image/jpeg;base64," + Data);
					if (selectedVideoMode.toLowerCase() === "recorded" && timestamp) {
						EventBus.publish("setCurrentRecordedTimestamp", { instanceId, time: new Date(timestamp) });
					}
					setMetadata(metadata);
				}
			})
			.then(sub => {
				setSubscription(sub);
			});
	};

	const checkLoop = timestamp => {
		if (clip && clip.current.end && new Date(timestamp) >= clip.current.end) {
			if (selectedVideoMode.toLowerCase() === "recorded" && clip.current.loop)
				setPlaybackTime(clip.current.start); // back to beginning if loop
			else {
				setPlaybackSpeed(0); // stop playback if not looping
				EventBus.publish("RecordedBottomBar_startVideo", { instanceId, playing: false });
			}
		}
	};

	const execPlaybackCommand = cmd => {
		if (cmd.instanceId !== instanceId) return;
		switch (cmd.command) {
			case "play":
				setPlaybackSpeed(cmd.speed || 1);
				EventBus.publish("RecordedBottomBar_startVideo", { instanceId, playing: true });
				break;
			case "pause":
				setPlaybackSpeed(0);
				break;
			case "setCursor":
				if (!videoStreamId.current) {
					clip.current.start = new Date(cmd.time);
					clip.current.end = new Date(new Date(new Date(cmd.time).getTime() + cmd.duration * 1000));
					clip.current.speed = cmd.recordedMode === "clip" ? cmd.speed || 1 : 1;
					clip.current.loop = !cmd.disableAutoLoop;
					EventBus.publish("RecordedBottomBar_startVideo", { instanceId, playing: clip.current.speed > 0 });
					playVideo();
				} else setPlaybackTime(cmd.time);
				break;
			case "setSpeed":
				setPlaybackSpeed(cmd.speed);
				break;
			default:
				console.log("Unknown playback command:", cmd.command);
		}
	};

	const setPlaybackSpeed = spd => {
		if (selectedVideoMode.toLowerCase() === "recorded" && videoStreamId.current) {
			cameraService.changeStream(cameraId, videoStreamId.current, { spd }, (err, response) => {
				if (err) console.log(err);
				if (!response) return;
			});
		}
	};

	const setPlaybackTime = time => {
		if (selectedVideoMode.toLowerCase() === "recorded" && videoStreamId.current) {
			cameraService.changeStream(
				cameraId,
				videoStreamId.current,
				{ time: new Date(time).toISOString() },
				(err, response) => {
					if (err) console.log(err);
					if (!response) return;
				}
			);
		}
	};

	const setPlayerDimensions = (playerWidth, playerHeight) => {
		if (videoStreamId.current) {
			cameraService.changeStream(
				cameraId,
				videoStreamId.current,
				{ playerWidth, playerHeight },
				(err, response) => {
					if (err) console.log(err);
					if (!response) return;
				}
			);
		}
	};

	const setupPlaybackStream = (cameraId, videoProfile, playbackStartTime) => {
		// -- Playback video
		cameraService.setupPlaybackStream(cameraId, videoProfile, playbackStartTime, (err, response) => {
			if (err) console.log(err);
			if (!response) return;

			// -- allow a null image for now, as milestone can't always access the exact time in the recorder service
			if (response.result && response.result.videoId) {
				const videoId = response.result.videoId;
				setVideoData(response.result.image || null);
				setPlaybackVideoId(videoId);
				setReplaySetupStream(false);
				dispatch(addReplayMedia(cameraId));
			} else {
				setError(true);
				setReplaySetupStream(false);
			}
		});

		// -- enable loading icon
		setReplaySetupStream(true);
	};

	const restartPlaybackStream = (cameraId, videoProfile, playbackStartTime, autoStart = false) => {
		// -- Playback video
		const t0 = performance.now();
		cameraService.setupPlaybackStream(cameraId, videoProfile, playbackStartTime, (err, response) => {
			if (err) console.log(err);
			if (!response || response.result === false) {
				setError(true);
				return console.log("Error restarting stream. Check camera-app logs for more details.");
			}

			const t1 = performance.now();
			console.log(`Time to restart playback stream took ${t1 - t0} milliseconds.`);

			// -- allow a null image for now, as milestone can't always access the exact time in the recorder service
			if (response.result && response.result.videoId) {
				const videoId = response.result.videoId;
				setVideoData(response.result.image || null);
				setPlaybackVideoId(videoId);
				setReplaySetupStream(false);
				if (autoStart) {
					startPlaybackStream(cameraId, videoId);
				}
			} else {
				setError(true);
				setReplaySetupStream(false);
			}
		});

		// -- enable loading icon
		setReplaySetupStream(true);
	};

	// This one specifically for replay - either need to rework or create new one
	const startPlaybackStream = (cameraId, videoId) => {
		// -- start stream and image retrieval stream
		cameraService.startPlaybackStream(cameraId, videoId, (err, response) => {
			if (err) console.log(err);
			if (!response || response.result === false) {
				return console.log("Error starting stream. Check camera-app logs for more details.");
			}

			if (!playbackStreamStarted) {
				cameraService
					.streamPlayback(cameraId, videoId, (err, response) => {
						if (err) console.log(err);
						if (!response) return;

						const { code, image } = response;
						if (code) {
							setRecoverPlaybackStreamState(true);
						} else if (image) {
							setError(false);
							setVideoData(image);
						}
					})
					.then(sub => {
						setSubscription(sub);
						setPlaybackStreamStarted(true);
					});
			}
		});
	};

	const pausePlaybackStream = (cameraId, videoId) => {
		// -- pause stream and stop image retrieval
		cameraService.pausePlaybackStream(cameraId, videoId, (err, response) => {
			if (err) console.log(err);
			if (!response) return;
		});
	};

	const styles = {
		wrapper: {
			height: "100%",
			width: "100%",
			display: "flex",
			alignItems: "center",
			placeContent: "center",
			justifyContent: "center"
		},
		video: {
			width: "100%",
			margin: "auto",
			maxWidth: "100%",
			objectFit: "contain",
			maxHeight: "100%",
			height: "100%"
		}
	};

	const handleImageChange = () => {
		const dim = imageRef.current ? [imageRef.current.offsetWidth, imageRef.current.offsetHeight] : null;
		if (imageChange) imageChange(metadata, dim);
	};

	const handleRetryStream = () => {
		setError(false);
		setVideoData(null);
		playVideo();
	};

	return (
		<div style={styles.wrapper} ref={playerDiv}>
			{displayProgress && !error && (subscription || replaySetupStream) && !videoData && <CircularProgress data-testid="orion-components-cbcomponents-video-wsimage-player-wsimage-player-circular-progress" />}
			{error && (
				<div
					style={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						background: "rgba(0,0,0,0.6)",
						zIndex: 1901
					}}
				>
					<StreamFailed retryStream={handleRetryStream} message={errorMessage} />
				</div>
			)}
			{videoData && (
				<img
					ref={imageRef}
					onLoad={handleImageChange}
					key="camera"
					alt="camera"
					src={videoData}
					style={styles.video} data-testid="orion-components-cbcomponents-video-wsimage-player-wsimage-player-img"
				/>
			)}
		</div>
	);
};

WSImagePlayer.propTypes = propTypes;
WSImagePlayer.defaultProps = defaultProps;

export default WSImagePlayer;
